import React, { useContext } from 'react';
import proptypes from 'prop-types';
import { Card, CardActions, Button } from '@one-thd/sui-atomic-components';
import classNames from 'classnames';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { ImpressionProvider } from '@thd-olt-component-react/impression';
import { dataModel } from '../../dataModels/dataModel';
import { QuotesProvider } from '../../context/QuotesContext';
import QuotesHeader from './QuotesHeader';
import QuotesNoTabsView from './QuotesNoTabsView';
import QuotesTabsView from './QuotesTabsView';
import QuotesNullView from './QuotesNullView/QuotesNullView';
import QuotesLoading from './QuotesLoading';
import QuotesError from './QuotesError';
import { useQuoteContext } from '../../hooks/useQuoteContext';
import { triggerAnalyticsCustomEvent } from '../../utils/analyticsEvents';

const { number, string, bool } = proptypes;

const trackViewAllClick = () => {
  triggerAnalyticsCustomEvent('quotes.stash-click', {
    component: 'view all'
  });
};

const QuotesActions = ({ isMobile = false }) => {
  return isMobile ? (
    <div>
      <CardActions>
        <Button onClick={trackViewAllClick} fullWidth href="/quote/list">View All Quotes</Button>
      </CardActions>
    </div>
  ) : null;
};

const QuotesContent = ({ isDashboardSnapshot = false, showStatusTabs = false, fillHeight = false }) => {
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';

  const {
    loading,
    error,
    hasQuotes,
    showTabs,
    quotes
  } = useQuoteContext();

  // Loading skeleton - only before there are any quotes to show. The query is
  // cache-and-network, so it reports loading on every remount while it refreshes in the
  // background; skeletoning then swapped cached cards out and back on each visit to a
  // Dashboard Snapshot tab (WOR2-1050).
  if (loading && !hasQuotes) {
    return <QuotesLoading showTabs={showStatusTabs} />;
  }

  // Error state
  if (error) {
    return <QuotesError />;
  }

  if (!hasQuotes) {
    return <QuotesNullView />;
  }

  // The tab bar brings its own bottom padding (QuotesTabPanels), so the outer stack
  // tightens to 16px when it's present to keep the header-to-cards distance unchanged.
  let quotesGapClass = isMobile ? 'sui-gap-6' : 'sui-gap-8';
  if (showTabs) {
    quotesGapClass = 'sui-gap-4';
  }

  const viewAllLabel = `View All Quotes (${quotes.length.toLocaleString()})`;

  return (
    <Card
      data-component="Quotes"
      data-testid="quotes-container"
      className={classNames('sui-h-full sui-w-full', { 'sui-rounded-md': isDashboardSnapshot })}
      // Dashboard Snapshot already supplies a single 16px inset around the active tab's
      // panel (split across ProAlertsAndUpdates' and SnapshotTabView's own wrappers, never
      // doubled between them). This Card's own sui-p-4 is correct for standalone usage
      // (Account Dashboard, etc.), where it's the only inset — but inside the snapshot it
      // stacked a second, redundant 16px on all four sides on top of the shared one. Your
      // Latest Orders has no Card of its own here and never had this extra layer, which is
      // why it was the one tab actually matching spec (flagged by both QE and UX).
      CardContentProps={{
        className: classNames(
          'sui-flex sui-overflow-hidden sui-gap-4 sui-flex-col sui-h-full',
          { 'sui-p-4': !isDashboardSnapshot }
        ),
      }}
      PaperProps={isDashboardSnapshot ? { variant: 'shadow', shadow: 'none' } : undefined}
    >
      <div className="sui-@container sui-h-full" id="quotes">
        <div className={
          classNames(
            'sui-flex sui-flex-col sui-h-full',
            quotesGapClass
          )
        }
        >
          {!isDashboardSnapshot && <QuotesHeader />}
          <div
            className={classNames('sui-flex sui-flex-col sui-grow', {
              // justify-between is what pushes mobile's CardActions to the bottom of the
              // card. For the snapshot view it also adds its own leftover-space gap on top
              // of the sui-mt-4 below, inflating the gap past 16px — dropping it here lets
              // sui-mt-4 be the only source of spacing, matching Projects/Orders exactly.
              'sui-justify-between': !isDashboardSnapshot,
            })}
          >
            {/*
              Matches the Figma "Carousel" spec: 24px top/bottom around the row of cards,
              desktop only (QuotesNoTabsView renders QuotesCompactList on mobile instead,
              with no equivalent spec). No left/right padding here — the shared 16px
              horizontal inset already comes from ProAlertsAndUpdates' own wrapper (the same
              one the tab bar and Footer button align to), so adding another layer here
              stacked to 32px and threw the cards out of alignment with them.
            */}
            {/* sui-grow (fillHeight) hands the carousel the card's full height, not its content's. */}
            <div className={classNames({
              'sui-pt-6 sui-pb-6': isDashboardSnapshot && !isMobile,
              'sui-grow': fillHeight
            })}
            >
              {showTabs ? <QuotesTabsView /> : <QuotesNoTabsView />}
            </div>
            {isDashboardSnapshot ? (
              // Matches the Figma "Footer" spec: 24px bottom padding (the space below the
              // button before the card's own edge), 16px right padding on the desktop
              // button only (it's right-aligned via justify-end; the mobile button is
              // fullWidth with no equivalent right inset in spec).
              //
              // sui-block sm:sui-hidden / sui-hidden sm:sui-block (a real viewport
              // breakpoint), not showBelowSmBreakpoint/showAboveSmBreakpoint (a container
              // query against this card's own width): the card's own width can cross the
              // container query's 384px threshold at a different point than the page's real
              // viewport does, so this button was flipping to the desktop/outline variant at
              // a different width than Orders' own View All button (which already uses a
              // viewport breakpoint) — visibly inconsistent side by side in the same
              // Dashboard Snapshot. Matching Orders' mechanism keeps all four tabs switching
              // at the same point.
              <div data-testid="quotes-snapshot-view-all" className="sui-pb-6">
                <div className="sui-block sm:sui-hidden">
                  <Button onClick={trackViewAllClick} href="/quote/list" fullWidth>{viewAllLabel}</Button>
                </div>
                <div className="sui-hidden sm:sui-block">
                  <div className="sui-flex sui-justify-end sui-pr-4">
                    <Button onClick={trackViewAllClick} href="/quote/list" variant="secondary">{viewAllLabel}</Button>
                  </div>
                </div>
              </div>
            ) : (
              <QuotesActions isMobile={isMobile} />
            )}
          </div>
        </div>
      </div>
    </Card>
  );
};

QuotesActions.propTypes = {
  isMobile: bool
};

QuotesContent.propTypes = {
  /**
   * Set true when rendering inside Dashboard Snapshot. Suppresses this component's own
   * "Quotes" title and drops the card border, since the snapshot's tabs and panel supply
   * those. View All still renders, moved below the quotes and labeled with the quote count,
   * replacing the mobile-only QuotesActions button.
   */
  isDashboardSnapshot: bool,
  /** See Quotes.showStatusTabs. */
  showStatusTabs: bool,
  fillHeight: bool
};

const Quotes = ({
  maxListSize = 3, componentId = '', isDashboardSnapshot = false, hideViewAllSuffix = false,
  showStatusTabs = false, fillHeight = false
}) => {

  const data = {
    name: 'Quotes',
    component: 'Quotes',
    type: 'content',
    position: '',
    id: componentId
  };

  return (
    <ImpressionProvider data={data}>
      <QuotesProvider
        maxListSize={maxListSize}
        componentId={componentId}
        isDashboardSnapshot={isDashboardSnapshot}
        hideViewAllSuffix={hideViewAllSuffix}
        showStatusTabs={showStatusTabs}
        fillHeight={fillHeight}
      >
        <QuotesContent
          isDashboardSnapshot={isDashboardSnapshot}
          showStatusTabs={showStatusTabs}
          fillHeight={fillHeight}
        />
      </QuotesProvider>
    </ImpressionProvider>
  );
};

Quotes.propTypes = {
  maxListSize: number,
  componentId: string,
  isDashboardSnapshot: bool,
  /**
   * Renders the header's View All link as just "View All" rather than "View All Quotes", for
   * consumers that already label the card (the account dashboard does). HEADER ONLY - the
   * mobile button keeps its full label, since mobile has no card title beside it to supply
   * the noun. Dashboard Snapshot's counted View All is also unaffected.
   */
  hideViewAllSuffix: bool,
  /**
   * Splits the quotes into "Active (n)" / "Expired (n)" tabs instead of one mixed list.
   * Opt-in: off by default, and ignored unless there is at least one quote on each side,
   * so a consumer that turns it on still gets the plain list when every quote shares a
   * status. Dashboard Snapshot leaves this off - its own tab bar supplies the switching.
   */
  showStatusTabs: bool,
  /**
   * Stretches the quote cards to the bottom of the card, for consumers that size the card
   * from outside (the account dashboard pairs it with Projects in one grid row). Off by
   * default: standalone, the extra height is just empty space. In Dashboard Snapshot it also
   * replaces the cards' 29rem floor: the snapshot sets the height, and the cards fill it.
   */
  fillHeight: bool
};

Quotes.displayName = 'Quotes';
Quotes.dataModel = dataModel;

export { Quotes, QuotesContent };
